import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { APP_DESCRIPTION_MAX, APP_NAME_MAX } from "../../lib/apps";
import { holderOptions } from "../../lib/holders";
import type { AppDetail, AppHolder } from "../../lib/types";
import { HolderSelect, useHolders } from "../holders/HolderSelect";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { Field } from "../ui/Input";
import { MicroLabel } from "../ui/Text";

/**
 * An app's name, description, and the group it acts for.
 *
 * One form for creating and editing, because the fields and their limits are the
 * same; `submit` is what differs. "Acts for" offers groups only: an app scoped
 * to a group sees that group's cards and sessions, and those of every group
 * inside it. Left empty, it sees every holder — said plainly under the field,
 * because that is the wider of the two and the one a partner should rarely get. A new app starts with no permissions and no
 * keys, so creating one grants nothing: the detail page is where it is given
 * something to read and a key to read it with.
 */

export interface AppFieldsProps {
  readonly heading: string;
  readonly submitLabel: string;
  readonly initial?: {
    readonly name: string;
    readonly description: string | null;
    readonly holder: AppHolder | null;
  };
  readonly submit: (body: {
    name: string;
    description: string | null;
    holderId: string | null;
  }) => Promise<AppDetail>;
  readonly onDone: (app: AppDetail) => void;
  readonly onCancel: () => void;
}

export function AppFields({
  heading,
  submitLabel,
  initial,
  submit,
  onDone,
  onCancel,
}: AppFieldsProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [holderId, setHolderId] = useState(initial?.holder?.id ?? "");
  const holders = useHolders();
  const groups = holderOptions(holders.data ?? []).filter((option) => option.type === "group");

  const save = useMutation({ mutationFn: submit, onSuccess: onDone });

  const trimmed = name.trim();
  const canSubmit = trimmed !== "" && trimmed.length <= APP_NAME_MAX && !save.isPending;

  return (
    <div className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>{heading}</MicroLabel>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {save.isError ? <ErrorNotice error={save.error} /> : null}

      <form
        aria-label={heading}
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          save.mutate({
            name: trimmed,
            description: description.trim() || null,
            holderId: holderId === "" ? null : holderId,
          });
        }}
        className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2"
      >
        <Field
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Fleet telematics partner"
          maxLength={APP_NAME_MAX}
          required
          autoFocus
          hint="Who is calling. Shown in the audit log beside everything done to this app."
        />
        <Field
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Optional — what they use the data for"
          maxLength={APP_DESCRIPTION_MAX}
        />
        <HolderSelect
          label="Acts for"
          value={holderId}
          onChange={setHolderId}
          options={groups}
          emptyLabel="Every holder"
          hint={
            holderId === ""
              ? "Sees every holder's cards and sessions. Pick a group to limit it to that group and the groups inside it."
              : "Sees this group's cards and sessions, and those of every group inside it — nothing else."
          }
        />
        <div className="flex items-end md:col-span-2">
          <Button type="submit" variant="primary" disabled={!canSubmit} pending={save.isPending}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}
